import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, LoaderCircle, XCircle } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';

import { ApiError } from '@/api/client';
import { useVerifyEmail } from '@/api/hooks/useAuthExtra';
import { Button } from '@/components/ui/button';

type Status = 'verifying' | 'success' | 'error';

/**
 * Landing page for the link in the verification email
 * (`{baseUrl}/verify-email?token=...&email=...`). Fires the verification
 * call itself on mount — there's nothing for the person to do but wait.
 */
export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const verifyEmail = useVerifyEmail();
  const [status, setStatus] = useState<Status>('verifying');
  const [error, setError] = useState<string | null>(null);

  const token = params.get('token');
  const email = params.get('email');

  // StrictMode/dev double-invokes effects; the token is single-use server
  // side, so a real second call would just fail on an otherwise-good link.
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    if (!token || !email) {
      setStatus('error');
      setError('This verification link is missing some details. Please use the link from your email.');
      return;
    }

    verifyEmail.mutate(
      { email, token },
      {
        onSuccess: () => setStatus('success'),
        onError: (err) => {
          setStatus('error');
          if (err instanceof ApiError) {
            setError(
              err.code === 'VERIFY_EXPIRED'
                ? 'This verification link has expired. Log in and request a new one.'
                : err.message,
            );
          } else {
            setError('Something went wrong. Please try again.');
          }
        },
      },
    );
    // Intentionally runs once on mount — see attempted.current above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm text-center">
        {status === 'verifying' && (
          <>
            <LoaderCircle className="mx-auto h-10 w-10 animate-spin text-primary" />
            <h1 className="mt-4 font-display text-2xl">Verifying your email…</h1>
            <p className="mt-2 text-sm text-muted-foreground">This will only take a moment.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" />
            <h1 className="mt-4 font-display text-2xl">Email verified</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Your email is confirmed. You can log in now.
            </p>
            <Button asChild className="mt-6 w-full" size="touch">
              <Link to="/login">Go to login</Link>
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="mx-auto h-10 w-10 text-status-expired" />
            <h1 className="mt-4 font-display text-2xl">Verification failed</h1>
            <p className="mt-2 text-sm text-muted-foreground">{error}</p>
            <Button asChild className="mt-6 w-full" size="touch">
              <Link to="/login">Back to login</Link>
            </Button>
          </>
        )}
      </div>
    </div>
  );
}